import heic2any from 'heic2any'
import Compressor from 'compressorjs'
import { useNotification } from './useNotification'
import StringMethods from '~/utils/character'
import { useResourcesApi } from '~/services/apis/resources'
import { TimeOut } from '~/services/apis/timeout'

export const useFileExplorer = () => {
	const { uploadFileApi } = useResourcesApi()
	const isDownloading = ref(false)
	const isIphoneFormat = (file: File) => {
		return file?.type.includes('/heic')
	}
	const readFile = (
		file: File | Blob,
		as: 'readAsText' | 'readAsArrayBuffer' | 'readAsBinaryString' | 'readAsDataURL',
		fn: (arg?: ProgressEvent) => void
	) => {
		const reader = new FileReader()
		reader.addEventListener('load', fn)

		switch (as) {
			case 'readAsArrayBuffer':
				reader.readAsArrayBuffer(file)
				break
			case 'readAsDataURL':
				reader.readAsDataURL(file)
				break
			case 'readAsBinaryString':
				reader.readAsBinaryString(file)
				break
			default:
				reader.readAsText(file)
				break
		}

		TimeOut(100).then(() => {
			reader.removeEventListener('load', fn)
		})
	}

	const getFileLink = (f: File | string) => {
		if (!f) {
			return ''
		}
		URL.revokeObjectURL(f as string)
		const fileSrc = URL.createObjectURL(f as File)
		return fileSrc
	}

	const compressImage = async (file: File) => {
		const fileData = ref<File>()
		const getFile = (result: File) => {
			fileData.value = result
		}
		const compressor = new Compressor(file, {
			quality: 0.8,
			maxWidth: 500,
			maxHeight: 500,
			convertSize: 1000,
			success: getFile,
			error(err: Error) {
				useNotification().alert({
					status: 400,
					message: 'Could not compress'
				})
				console.error('Could not compress - ' + err)
			}
		})
		await TimeOut()
		return { compressor, fileData }
	}
	const iphoneImgConverter = async (file: File) => {
		// This checks for iphone image formats
		if (isIphoneFormat(file)) {
			const result = (await heic2any({
				blob: file,
				toType: 'image/jpeg',
				quality: 0.5
			})) as Blob
			const { stringGenerator } = StringMethods()
			return new File([result], `${stringGenerator(10)}.jpg`, {
				lastModified: Date.now(),
				type: 'image/jpeg'
			})
		}
	}
	const uploadImage = async (e: Event, useServer = false, format = '.jpg,jpeg,.png,.jpeg') => {
		const target = e.target as HTMLInputElement

		// const fileBlob = shallowRef<File | null>(null)
		const result = ref<{
			loading: boolean
			src: string
			blob: File | null
		}>({
			loading: true,
			src: '',
			blob: null
		})
		try {
			if (!target.files) {
				return null
			} else {
				// This is limited to just a single file
				const file = target.files[0]
				if (!file) {
					return null
				}
				const blobFile = shallowRef<File | null>(null)
				const fileType = String(file.type).split('/')[1] || ''
				// Check for IOS image type format
				if (file.type.includes('/heic')) {
					const res = await iphoneImgConverter(file)
					blobFile.value = res as File
				}

				if (!format.replaceAll('.', '').split(',').includes(fileType)) {
					return useNotification().alert({
						status: 400,
						message: 'Invalid file format'
					})
				}
				// Limit file size to 2mb max
				if (Number(file.size) > 2000000) {
					result.value.loading = true
					compressImage(file)
						.then(({ fileData }) => {
							if (fileData.value && fileData.value?.size > 2000000) {
								return uploadImage(e, useServer, format)
							}
							if (fileData.value) {
								blobFile.value = fileData.value
							}
						})
						.finally(() => {
							result.value.loading = false
						})
				} else {
					blobFile.value = file
				}
				if (blobFile.value) {
					if (useServer) {
						const fileInstance = new FormData()
						result.value.loading = true
						fileInstance.append('file', blobFile.value)
						const res = await uploadFileApi(fileInstance)
						if (res.value?.data.file_url) {
							result.value.blob = blobFile.value
							result.value.src = res.value.data.file_url
						} else {
							useNotification().alert({
								status: res.value?.responseCode || 400,
								message: res.value?.responseText || ''
							})
						}
					} else {
						const fileSrc = getFileLink(blobFile.value)
						result.value.blob = blobFile.value
						result.value.src = fileSrc
					}
				} else {
					useNotification().alert({
						status: 400,
						message: 'File upload failed'
					})
				}
			}
		} finally {
			result.value.loading = false
		}
		return result
	}
	const openFile = (e: MaybeRefOrGetter<HTMLInputElement | null>) => {
		const target = toValue(e)
		if (target) {
			target.value = ''
			target.click()
		}
	}
	const downloadFileResource = (file: string, file_name: string) => {
		if (file) {
			// Create a link element
			const link = document.createElement('a')

			// Set the href attribute to the blob URL
			link.href = file
			// Set the download attribute to the specified filename
			link.download = file_name

			// Simulate a click on the link to trigger the download
			link.click()
			link.remove()
			useNotification().alert({
				status: 200,
				message: `File downloaded successfully as ${file_name}`
			})
		}
	}
	const downloadFile = async (url: string, file_name: string) => {
		try {
			// Fetch the image using the provided URL
			const response = await useFetch('/api/download-file', {
				method: 'POST',
				retry: false,
				body: JSON.stringify({
					src: url
				})
			})
			const blob = response.data.value
			if (blob) {
				const imageUrl = URL.createObjectURL(blob as unknown as Blob)
				downloadFileResource(imageUrl, file_name)
			}
		} catch (error) {
			console.error('Error downloading image:', JSON.stringify(error))
			useNotification().alert({
				status: 400,
				message: 'Error downloading image'
			})
		}
	}
	// const downloadFile = async (source: string, filename: string) => {
	//     resolve(async () => {
	//         const result = await fetch(source)
	//         const blob = await result.blob()
	//         const href = URL.createObjectURL(blob)
	//         const anchorElement = document.createElement('a')
	//         anchorElement.href = href
	//         anchorElement.download = filename
	//         document.body.appendChild(anchorElement)
	//         anchorElement.click()
	//         document.body.removeChild(anchorElement)
	//         window.URL.revokeObjectURL(href)
	//     }, isDownloading).catch(error => {
	//         console.error('Error downloading image:', JSON.stringify(error))
	//         useNotification().alert({
	//             status: 400,
	//             message: 'Error downloading image'
	//         })
	//     })
	// }

	return {
		readFile,
		uploadImage,
		openFile,
		downloadFile,
		downloadFileResource,
		getFileLink,
		isDownloading
	}
}
